import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { SearchInput } from '@/components/design-system/molecules/search-input';
import { DatePicker } from '@/components/design-system/molecules/datepicker';
import { useContractsListFiltersContext } from '../../../../hooks/use-get-contracts-list-filter';
import {
  ResidencyRegistrationStatus,
  ContractStatusOption,
} from '@/features/admin/contracts/types';
import { PropertyManagersFilter } from './components/property-managers-filter';
import { StatusFilter } from './components/status-filter';
import { TextInput } from '@/components/design-system/atoms/text/text-input';

const ContractsListFilter: React.FC = () => {
  const { t } = useTranslation();
  const {
    searchQuery,
    setSearchQuery,
    moveInStartDate,
    moveInEndDate,
    moveOutStartDate,
    moveOutEndDate,
    setMoveInDateRange,
    setMoveOutDateRange,
    residencyRegistrationStatus,
    setResidencyRegistrationStatus,
    roomNumber,
    setRoomNumber
  } = useContractsListFiltersContext();

  const residencyOptions: ContractStatusOption[] = [
    { slug: ResidencyRegistrationStatus.TO_DO, displayValue: 'To Do' },
    { slug: ResidencyRegistrationStatus.PENDING, displayValue: 'In Progress' },
    { slug: ResidencyRegistrationStatus.OVERDUE, displayValue: 'Overdue' },
    { slug: ResidencyRegistrationStatus.DONE, displayValue: 'Done' },
  ];

  return (
    <div className="flex flex-row flex-wrap items-start gap-4 py-3">
      <SearchInput
        placeholder={t('js.features.admin.contracts.room_assignment.search_placeholder') ?? ''}
        className="w-[250px]"
        label={t('js.features.admin.contracts.room_assignment.search_label') ?? ''}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <div className="flex min-w-[230px] flex-col">
        <DatePicker
          placeholder="Move in"
          className="w-[230px]"
          onChange={setMoveInDateRange}
          label={'Move-in'}
          dateRange
          type="date"
          startValue={moveInStartDate}
          endValue={moveInEndDate}
        />
      </div>

      <div className="flex min-w-[230px] flex-col">
        <DatePicker
          placeholder="Move out"
          className="w-[230px]"
          onChange={setMoveOutDateRange}
          label={'Move-out'}
          dateRange
          type="date"
          startValue={moveOutStartDate}
          endValue={moveOutEndDate}
        />
      </div>

      <TextInput
        label="Room Number"
        type="text"
        value={roomNumber}
        onChange={(e) => setRoomNumber(e.target.value)}
        placeholder="Room number"
        containerClassName="max-w-[130px]"
      />

      <StatusFilter />

      <div className="flex min-w-[100px] flex-col">
        <DropDown
          value={residencyRegistrationStatus}
          onChange={setResidencyRegistrationStatus}
          placeholder="Select status"
          label="Residency Status"
          showLabel
          className="w-full"
          displayValue={residencyRegistrationStatus?.displayValue}
          inputWrapperClassName="pr-[0]"
        >
          <DropDown.DropDownOptions>
            {residencyOptions.map((option) => (
              <DropDown.DropDownOption key={option.slug} value={option}>
                <DropDown.DropDownOptionDefaultContent>
                  {option.displayValue}
                </DropDown.DropDownOptionDefaultContent>
              </DropDown.DropDownOption>
            ))}
          </DropDown.DropDownOptions>
        </DropDown>
      </div>

      <PropertyManagersFilter />
    </div>
  );
};

export { ContractsListFilter };
